import type { BitriseSetup } from '@/api/schemas';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { WebhookStatus } from '@/features/apps/WebhookStatus';
import type { Can } from '@/lib/permissions';

function AppLink({ data }: { data: BitriseSetup }) {
  if (!data.bitriseAppUrl) return <>{data.bitriseAppSlug}</>;
  return (
    <a href={data.bitriseAppUrl} target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">
      {data.bitriseAppSlug}
    </a>
  );
}

function DoneFacts({ data }: { data: BitriseSetup }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
      <dt className="text-subtle">Bitrise app</dt>
      <dd className="min-w-0 truncate font-mono">
        <AppLink data={data} />
        {data.reused ? <span className="text-subtle"> · existing app</span> : null}
      </dd>
      <dt className="text-subtle">Repository</dt>
      <dd className="min-w-0 truncate font-mono">{data.repository ? `${data.repository.workspace}/${data.repository.slug}` : '—'}</dd>
      <dt className="text-subtle">Workflows</dt>
      <dd className="flex min-w-0 flex-wrap gap-1">
        {data.workflows.map((w) => (
          <Tag key={w}>{w}</Tag>
        ))}
      </dd>
      <dt className="text-subtle">Webhook</dt>
      <dd className="min-w-0">
        <WebhookStatus webhook={data.webhook} provider="Bitrise" />
      </dd>
    </dl>
  );
}

function StaleNote({ stale }: { stale: boolean }) {
  if (!stale) return <p className="text-xs text-subtle">The workflows on Bitrise match this app and its profiles.</p>;
  return (
    <Alert data-testid="bitrise-stale" className="px-3 py-2">
      <AlertDescription className="text-[13px]">The profiles changed since the workflows were stored on Bitrise. Sync them so builds use the new workflows.</AlertDescription>
    </Alert>
  );
}

interface Props {
  data: BitriseSetup;
  admin: Can;
  syncing: boolean;
  onSync: () => void;
  /** The "Done" button of the finished state (Connect app closes its sheet). */
  onDone?: () => void;
}

/** The finished state: the Bitrise app, its repository and workflows, and "Sync Bitrise workflows". */
export function BitriseDone({ data, admin, syncing, onSync, onDone }: Props) {
  return (
    <div className="flex flex-col gap-3">
      <DoneFacts data={data} />
      <StaleNote stale={Boolean(data.configStale)} />
      {data.warnings.map((w) => (
        <p key={w} className="text-xs text-awaiting">{w}</p>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <GatedButton size="sm" variant={data.configStale ? 'default' : 'outline'} data-testid="bitrise-sync" can={admin} loading={syncing} onClick={onSync}>
          <Icon name="refresh" size={16} />
          Sync Bitrise workflows
        </GatedButton>
        {onDone ? (
          <Button size="sm" variant="ghost" onClick={onDone}>
            Done
          </Button>
        ) : null}
      </div>
    </div>
  );
}
